import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/hooks/useTranslation';
import { Capsule } from '@/services/capsulesApi';
import M_ContentCard from '@/components/M_Molecules/M_ContentCard/M_ContentCard';
import O_CapsulePost from '../O_Organisms/O_CapsulePost/O_CapsulePost';
import { W_MasonryGrid } from '../W_Wrappers/W_MasonryGrid/W_MasonryGrid';
import O_CapsulePostHeaderResponsive from '../O_Organisms/O_CapsulePostHeader/O_CapsulePostHeaderResponsive';
import O_CapsuleLayout from '../O_Organisms/O_CapsuleLayout/O_CapsuleLayout';
import styles from './CapsuleContentView.module.css';
import M_SelectPicker from '../M_Molecules/M_SelectPicker/M_SelectPicker';
import Q_BlurEffect from '../Q_Quarks/Q_BlurEffect/Q_BlurEffect';
import cn from 'classnames';
import { CapsuleViewMode, getCapsuleViewOptions } from './constants';
import { useIsMobile } from '@/hooks/useIsMobile';
import O_CapsulePostMenu from '../O_Organisms/O_CapsulePostMobile/O_CapsulePostMobile';
import { useAuth } from '@/contexts/AuthContext';
import { useDeleteCapsule } from '@/hooks/useCapsules';
import { PATHS } from '@/constants/paths';

interface CapsuleContentViewProps {
  capsule: Capsule;
  isFullscreen: boolean;
  selectedView: CapsuleViewMode;
  onViewSelect: (option: CapsuleViewMode) => void;
}

const MobileDescriptionCard: React.FC<{
  title: string;
  text: string;
  t: (key: string) => string;
}> = ({ title, text, t }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const safeTitle = (title || '').trim();
  const safeText = (text || '').trim();

  const canToggle =
    safeText.length > 180 || safeText.includes('\n') || safeText.includes('  ');

  return (
    <div className={styles.mobile_description_card}>
      {safeTitle && (
        <h3 className={styles.mobile_description_title}>{safeTitle}</h3>
      )}
      {safeText && (
        <pre
          className={cn(
            styles.mobile_description_text,
            !isExpanded &&
              canToggle &&
              styles.mobile_description_text_collapsed,
            'text-p-2'
          )}
        >
          {safeText}
        </pre>
      )}
      {canToggle && safeText && (
        <button
          type='button'
          className={styles.mobile_description_toggle}
          onClick={() => setIsExpanded(v => !v)}
        >
          {isExpanded ? t('common.collapse') : t('common.more')}
        </button>
      )}
    </div>
  );
};

export const CapsuleContentView: React.FC<CapsuleContentViewProps> = ({
  capsule,
  isFullscreen,
  selectedView,
  onViewSelect,
}) => {
  const t = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const deleteCapsuleMutation = useDeleteCapsule();
  const contentItems = capsule.content;
  const [isPostExpanded, setIsPostExpanded] = useState(true);
  const [isPostMenuOpen, setIsPostMenuOpen] = useState(false);
  const isMobile = useIsMobile(1080);

  const isAuthor = Boolean(
    user?.id &&
      capsule.author?.id &&
      String(capsule.author.id) === String(user.id)
  );

  const handleDeleteCapsule = useCallback(async () => {
    try {
      await deleteCapsuleMutation.mutate(String(capsule.id));
      navigate(PATHS.CAPSULES, { state: { notification: 'deleteSuccess' } });
    } catch {
      // ошибка удаления
    }
  }, [capsule.id, deleteCapsuleMutation, navigate]);

  const viewOptions = useMemo(() => getCapsuleViewOptions(t), [t]);
  const selectedOptionLabel = useMemo(
    () =>
      viewOptions.find(({ key }) => key === selectedView)?.label ||
      viewOptions[0]?.label ||
      '',
    [selectedView, viewOptions]
  );

  const handleViewSelect = useCallback(
    (option: string) => {
      const nextView = viewOptions.find(({ label }) => label === option)?.key;
      if (nextView) {
        onViewSelect(nextView);
      }
    },
    [onViewSelect, viewOptions]
  );

  const postText = useMemo(() => {
    const textItems = (contentItems || [])
      .filter((it: any) => it.type === 'text' && typeof it.text === 'string')
      .map((it: any) => it.text as string);
    return capsule.description || textItems.join('\n');
  }, [capsule.description, contentItems]);

  const shouldShowMobileDescriptionCard = useMemo(() => {
    if (!isMobile) return false;
    return (
      Boolean((capsule.title || '').trim()) || Boolean((postText || '').trim())
    );
  }, [capsule.title, isMobile, postText]);

  const masonryChildren = useMemo(() => {
    const items = Array.isArray(contentItems) ? contentItems : [];

    if (items.length === 0) {
      return shouldShowMobileDescriptionCard
        ? [
            <div key='__capsule_description__'>
              <MobileDescriptionCard
                title={capsule.title || ''}
                text={postText}
                t={t}
              />
            </div>,
          ]
        : [];
    }

    const result: React.ReactNode[] = [];
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      result.push(<M_ContentCard key={item.id} content={item as any} />);

      if (i === 0 && shouldShowMobileDescriptionCard) {
        result.push(
          <div key='__capsule_description__'>
            <MobileDescriptionCard
              title={capsule.title || ''}
              text={postText}
              t={t}
            />
          </div>
        );
      }
    }
    return result;
  }, [
    capsule.title,
    contentItems,
    postText,
    shouldShowMobileDescriptionCard,
    t,
  ]);

  const capsulePostData = useMemo(() => {
    const coauthorAvatars =
      capsule.privacy?.coauthors
        ?.filter(c => c.avatar)
        .map(c => c.avatar!)
        .slice(0, 3) || [];

    return {
      capsulePostAuthorDate: {
        avatars: coauthorAvatars,
        coauthors: capsule.privacy?.coauthors?.length || 0,
        date: capsule.meta.createdAt || '',
        isMyCapsule: false,
        ownerName: capsule.author.name || '',
        ownerId: capsule.author.id,
      },
      capsulePostContent: {
        title: capsule.title || '',
        text: postText,
        commentCount: capsule.engagement.comments || 0,
        plantCount: 0,
        indicator: 'comment' as const,
      },
      treeId: capsule.tree?.id,
    };
  }, [capsule, postText]);

  const togglePostExpanded = () => {
    setIsPostExpanded(!isPostExpanded);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.content_wrapper}>
        <div className={styles.header_wrapper}>
          <O_CapsulePostHeaderResponsive
            onToggleExpanded={togglePostExpanded}
            isExpanded={isPostExpanded}
            onOpenPostMenu={() => setIsPostMenuOpen(true)}
            selectedView={selectedView}
            onViewSelect={onViewSelect}
            showActionsMenu={isAuthor}
            onDelete={isAuthor ? handleDeleteCapsule : undefined}
            capsuleId={String(capsule.id)}
          />
          {!isPostExpanded && !isMobile && (
            <div className={styles.capsule_layout_wrapper}>
              <O_CapsuleLayout
                title={capsule.title || ''}
                commentCount={capsule.engagement.comments || 0}
                plantCount={0}
              />
            </div>
          )}
        </div>
        <div className={styles.content_grid_wrapper}>
          <W_MasonryGrid>{masonryChildren}</W_MasonryGrid>
        </div>
        <div className={styles.bottom_wrapper}>
          <div
            className={cn(styles.select_picker_wrapper, 'effect-green-glow')}
          >
            <M_SelectPicker
              options={viewOptions.map(({ label }) => label)}
              selectedOption={selectedOptionLabel}
              onSelect={handleViewSelect}
              background='white'
            />
          </div>
          <Q_BlurEffect />
        </div>
      </div>
      {isPostExpanded && !isMobile && (
        <div className={styles.post_wrapper}>
          <O_CapsulePost
            capsule={capsulePostData as any}
            showActionsMenu={isAuthor}
            onDelete={isAuthor ? handleDeleteCapsule : undefined}
            capsuleId={String(capsule.id)}
          />
        </div>
      )}
      <O_CapsulePostMenu
        isOpen={isPostMenuOpen}
        onClose={() => setIsPostMenuOpen(false)}
        capsule={capsulePostData as any}
        showActionsMenu={isAuthor}
        onDelete={isAuthor ? handleDeleteCapsule : undefined}
        capsuleId={String(capsule.id)}
      />
    </div>
  );
};
